import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import {
  X, MapPin, ExternalLink, Paperclip, CheckCircle2, XCircle, Video,
  Sparkles, Inbox, GraduationCap, CalendarDays, Clock, Tag, Package,
  Puzzle, Zap, Users, FileText, Handshake, Target, Plane, Wrench, Clipboard,
} from 'lucide-react';
import PageShell from '../components/layout/PageShell';

const API_URL = process.env.REACT_APP_API_URL;
const ROLES_CLAIM = 'https://raah-portal.com/roles';

// Workshop.date is stored as a datetime-local value ("YYYY-MM-DDTHH:mm"); format
// for display, falling back to the raw value for older free-text dates.
function formatWorkshopDate(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}

const STATUS_STYLES = {
  'Submitted':   { background: '#fff9db', color: '#e67700', border: '1px solid #ffe066' },
  'In Review':   { background: '#e8f4fd', color: '#1971c2', border: '1px solid #a5d8ff' },
  'In Progress': { background: '#ebfbee', color: '#2f9e44', border: '1px solid #b2f2bb' },
  'Completed':   { background: '#f3f0ff', color: '#5f3dc4', border: '1px solid #d0bfff' },
  'Cancelled':   { background: '#fff5f5', color: '#c92a2a', border: '1px solid #ffc9c9' },
  'Pending':     { background: '#fff9db', color: '#e67700', border: '1px solid #ffe066' },
  'Confirmed':   { background: '#ebfbee', color: '#2f9e44', border: '1px solid #b2f2bb' },
  'Declined':    { background: '#fff5f5', color: '#c92a2a', border: '1px solid #ffc9c9' },
};

const TYPE_ICON = {
  'Pre-Sales': Target, 'In-Flight Deal': Plane, 'Sold Deal': CheckCircle2, 'Break-Fix/Post-Sales': Wrench,
};

const TYPE_META = {
  engagement: { icon: Handshake,     label: 'Engagement' },
  material:   { icon: FileText,      label: 'Material Request' },
  workshop:   { icon: GraduationCap, label: 'Workshop' },
  booking:    { icon: CalendarDays,  label: 'Booking' },
};

const ENG_FILTERS = ['All', 'Submitted', 'In Review', 'In Progress', 'Completed', 'Cancelled'];
const MAT_FILTERS = ['All', 'Submitted', 'In Review', 'In Progress', 'Completed', 'Cancelled'];
const WS_FILTERS  = ['All', 'Pending', 'Confirmed', 'Declined', 'Cancelled'];

const s = {
  tabs:        { display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #eee' },
  tab:         (a) => ({ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 20px', fontSize: '13px', fontWeight: a ? '700' : '500', color: a ? '#fd5f0d' : '#888', cursor: 'pointer', background: 'none', border: 'none', borderBottom: a ? '2px solid #fd5f0d' : '2px solid transparent', marginBottom: '-2px' }),
  filters:     { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  filterBtn:   (a) => ({ padding: '5px 14px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', fontWeight: a ? '600' : '400', border: `1px solid ${a ? '#fd5f0d' : '#ddd'}`, background: a ? '#fef0e6' : '#fff', color: a ? '#fd5f0d' : '#555' }),
  grid:        { display: 'flex', flexDirection: 'column', gap: '12px' },
  card:        { background: '#fff', border: '1px solid #eee', borderRadius: '10px', padding: '16px 20px', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  cardHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' },
  cardTitle:   { fontSize: '14px', fontWeight: '600', color: '#111', margin: 0 },
  cardMeta:    { display: 'flex', gap: '12px', fontSize: '12px', color: '#888', flexWrap: 'wrap', marginBottom: '8px' },
  metaItem:    { display: 'inline-flex', alignItems: 'center', gap: '4px' },
  statusBadge: (st) => ({ ...(STATUS_STYLES[st] || STATUS_STYLES['Submitted']), padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }),
  summary:     { fontSize: '13px', color: '#555', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  planeLink:   { fontSize: '12px', color: '#fd5f0d', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '8px' },
  trackBtn:    { padding: '6px 14px', background: '#fd5f0d', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '8px' },
  empty:       { textAlign: 'center', padding: '60px 20px', color: '#999' },
  emptyIcon:   { display: 'flex', justifyContent: 'center', color: '#ccc', marginBottom: '12px' },
  emptyText:   { fontSize: '14px', marginBottom: '16px' },
  emptyBtn:    { padding: '8px 20px', background: '#fd5f0d', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', textDecoration: 'none', display: 'inline-block' },
  overlay:     { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal:       { background: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '560px', width: '100%', maxHeight: '80vh', overflowY: 'auto', position: 'relative' },
  modalClose:  { position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', color: '#999', display: 'flex' },
  modalTitle:  { fontSize: '16px', fontWeight: '700', marginBottom: '4px' },
  modalSub:    { fontSize: '12px', color: '#888', marginBottom: '20px' },
  modalIcon:   { color: '#555', marginBottom: '2px' },
  fieldGrid:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  fieldLabel:  { fontSize: '11px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' },
  fieldValue:  { fontSize: '13px', color: '#222', marginTop: '2px' },
  divider:     { borderTop: '1px solid #eee', margin: '16px 0' },
  loading:     { textAlign: 'center', padding: '40px', color: '#888', fontSize: '14px' },
  error:       { display: 'flex', alignItems: 'center', gap: '6px', padding: '12px 16px', background: '#fdecea', border: '1px solid #f5c2be', borderRadius: '6px', color: '#b00020', fontSize: '13px' },
};

function FieldItem({ label, value }) {
  if (!value) return null;
  return <div style={{ marginBottom: '4px' }}><div style={s.fieldLabel}>{label}</div><div style={s.fieldValue}>{value}</div></div>;
}

function EngagementModal({ item, onClose, isAdmin }) {
  const navigate = useNavigate();
  const TypeIcon = TYPE_ICON[item.engagementType] || Clipboard;
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <button style={s.modalClose} onClick={onClose}><X size={18} /></button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          <TypeIcon size={22} strokeWidth={1.75} style={s.modalIcon} />
          <div style={s.modalTitle}>{item.accountName}</div>
        </div>
        <div style={s.modalSub}>Submitted {new Date(item.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} · <span style={s.statusBadge(item.status)}>{item.status}</span></div>
        <a href={`/my-requests/${item._id}/track`} style={{ ...s.trackBtn, marginBottom: '16px' }}
          onClick={ev => { ev.preventDefault(); navigate(`/my-requests/${item._id}/track`); }}>
          <MapPin size={14} /> Track Engagement
        </a>
        <div style={s.fieldGrid}>
          <FieldItem label="Engagement Type" value={item.engagementType} />
          <FieldItem label="Product" value={item.product} />
          <FieldItem label="Use Case" value={item.useCase} />
          <FieldItem label="RAAH Product" value={item.raahProduct} />
          <FieldItem label="Urgency" value={item.urgency} />
          <FieldItem label="Timeline" value={item.timeline} />
          <FieldItem label="Stakeholders" value={item.stakeholders} />
        </div>
        <div style={s.divider} />
        <FieldItem label="Opportunity Summary" value={item.opportunitySummary} />
        {item.notes && <><div style={{ height: '10px' }} /><FieldItem label="Notes" value={item.notes} /></>}
        {isAdmin && item.planeIssueUrl && <><div style={s.divider} /><a href={item.planeIssueUrl} target="_blank" rel="noreferrer" style={s.planeLink}><ExternalLink size={12} /> View in Plane #{item.planeSequenceId}</a></>}
      </div>
    </div>
  );
}

function MaterialRequestModal({ item, onClose, isAdmin }) {
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <button style={s.modalClose} onClick={onClose}><X size={18} /></button>
        <FileText size={22} strokeWidth={1.75} style={s.modalIcon} />
        <div style={s.modalTitle}>{item.assetType}</div>
        <div style={s.modalSub}>Submitted {new Date(item.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} · <span style={s.statusBadge(item.status)}>{item.status}</span></div>
        <div style={s.fieldGrid}>
          <FieldItem label="Asset Type" value={item.assetType} />
          <FieldItem label="Deadline" value={item.deadline} />
          <FieldItem label="Use Case" value={item.useCase} />
        </div>
        <div style={s.divider} />
        <FieldItem label="Customer / Context" value={item.customerContext} />
        {item.fulfilledMaterialId && (
          <>
            <div style={s.divider} />
            <div style={s.fieldLabel}>Material</div>
            <a href={item.fulfilledMaterialId.downloadUrl} target="_blank" rel="noreferrer" style={s.planeLink}>
              <Paperclip size={12} /> Download {item.fulfilledMaterialId.title}
            </a>
          </>
        )}
        {isAdmin && item.planeIssueUrl && <><div style={s.divider} /><a href={item.planeIssueUrl} target="_blank" rel="noreferrer" style={s.planeLink}><ExternalLink size={12} /> View in Plane #{item.planeSequenceId}</a></>}
      </div>
    </div>
  );
}

function WorkshopModal({ item, onClose }) {
  const ws = item.workshopId || {};
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <button style={s.modalClose} onClick={onClose}><X size={18} /></button>
        <GraduationCap size={22} strokeWidth={1.75} style={s.modalIcon} />
        <div style={s.modalTitle}>{item.workshopTitle || ws.title}</div>
        <div style={s.modalSub}>Registered {new Date(item.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} · <span style={s.statusBadge(item.status)}>{item.status}</span></div>
        <div style={s.fieldGrid}>
          <FieldItem label="Date" value={formatWorkshopDate(ws.date)} />
          <FieldItem label="Location" value={ws.displayLocation} />
          <FieldItem label="Product" value={ws.product} />
          <FieldItem label="Segment" value={ws.segment} />
          <FieldItem label="Your Name" value={item.fullName} />
          <FieldItem label="Your Role" value={item.role} />
        </div>
        {ws.description && <><div style={s.divider} /><FieldItem label="About this Workshop" value={ws.description} /></>}
        {item.status === 'Confirmed' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '16px', padding: '12px', background: '#ebfbee', borderRadius: '8px', fontSize: '13px', color: '#2f9e44' }}>
            <CheckCircle2 size={16} /> Your spot is confirmed. Check your email for full details including location and joining instructions.
          </div>
        )}
      </div>
    </div>
  );
}

function BookingModal({ item, getToken, onClose, onCancelled }) {
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');

  const canCancel = item.status !== 'Cancelled' && item.status !== 'Completed';

  const handleCancel = async () => {
    if (!window.confirm('Cancel this meeting? This will remove it from the Teams calendar and cannot be undone.')) return;
    setCancelling(true); setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}api/booking/${item._id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'Cancelled' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to cancel meeting.');
      onCancelled(item._id);
      onClose();
    } catch (err) { setError(err.message); }
    finally { setCancelling(false); }
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <button style={s.modalClose} onClick={onClose}><X size={18} /></button>
        <CalendarDays size={22} strokeWidth={1.75} style={s.modalIcon} />
        <div style={s.modalTitle}>{item.topic}</div>
        <div style={s.modalSub}>
          {new Date(item.preferredTime).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })} · <span style={s.statusBadge(item.status)}>{item.status}</span>
        </div>
        {error && <div style={{ ...s.error, marginBottom: '16px' }}><XCircle size={14} /> {error}</div>}
        <div style={s.fieldGrid}>
          <FieldItem label="Team" value={item.teamLabel} />
          <FieldItem label="Duration" value={item.durationMinutes ? `${item.durationMinutes} minutes` : ''} />
        </div>
        {item.notes && <><div style={s.divider} /><FieldItem label="Notes" value={item.notes} /></>}
        {item.joinUrl && (
          <>
            <div style={s.divider} />
            <a href={item.joinUrl} target="_blank" rel="noreferrer" style={s.planeLink}><Video size={14} /> Join Teams Meeting</a>
          </>
        )}
        {canCancel && (
          <>
            <div style={s.divider} />
            <button
              style={{ padding: '8px 18px', background: 'none', color: '#c92a2a', border: '1px solid #ffc9c9', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
              onClick={handleCancel} disabled={cancelling}
            >
              {cancelling ? 'Cancelling…' : 'Cancel Meeting'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function FilterBar({ items, filters, filter, setFilter, statusKey = 'status' }) {
  return (
    <div style={s.filters}>
      {filters.map(f => (
        <button key={f} style={s.filterBtn(filter === f)} onClick={() => setFilter(f)}>
          {f}{f === 'All' ? ` (${items.length})` : items.filter(i => i[statusKey] === f).length > 0 ? ` (${items.filter(i => i[statusKey] === f).length})` : ''}
        </button>
      ))}
    </div>
  );
}

export default function MyRequests() {
  const navigate = useNavigate();
  const { getAccessTokenSilently, user } = useAuth0();
  const roles = user?.[ROLES_CLAIM] || [];
  const isAdmin = roles.includes('Partner Portal Admin');
  const [tab, setTab] = useState('engagements');

  const [engagements,  setEngagements]  = useState([]);
  const [matRequests,  setMatRequests]  = useState([]);
  const [workshops,    setWorkshops]    = useState([]);
  const [bookings,     setBookings]     = useState([]);
  const [bookFilter,   setBookFilter]   = useState('All');
  const [bookSelected, setBookSelected] = useState(null);

  const [engFilter,    setEngFilter]    = useState('All');
  const [matFilter,    setMatFilter]    = useState('All');
  const [wsFilter,     setWsFilter]     = useState('All');

  const [engSelected,  setEngSelected]  = useState(null);
  const [matSelected,  setMatSelected]  = useState(null);
  const [wsSelected,   setWsSelected]   = useState(null);

  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  const getToken = () => getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
  });

  useEffect(() => {
    async function fetchAll() {
      try {
        const token   = await getToken();
        const headers = { Authorization: `Bearer ${token}` };
        const [engRes, matRes, wsRes, bookRes] = await Promise.all([
          fetch(`${API_URL}api/profile/engagements`,         { headers }),
          fetch(`${API_URL}api/profile/material-requests`,   { headers }),
          fetch(`${API_URL}api/workshops/my/registrations`,  { headers }),
          fetch(`${API_URL}api/booking/my`,                  { headers }),
        ]);
        const [engData, matData, wsData, bookData] = await Promise.all([engRes.json(), matRes.json(), wsRes.json(), bookRes.json()]);
        if (!engRes.ok) throw new Error(engData.error);
        if (!matRes.ok) throw new Error(matData.error);
        setEngagements(engData.engagements   || []);
        setMatRequests(matData.requests      || []);
        setWorkshops(wsData.registrations    || []);
        setBookings(bookData.bookings        || []);
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    }
    fetchAll();
  }, []);

  const filteredEng = engFilter === 'All' ? engagements : engagements.filter(e => e.status === engFilter);
  const filteredMat = matFilter === 'All' ? matRequests : matRequests.filter(r => r.status === matFilter);
  const filteredWs  = wsFilter  === 'All' ? workshops   : workshops.filter(w => w.status  === wsFilter);

  // "Active" = not yet in a terminal state, consistent with each tab's own status set.
  const activeEngagements = engagements.filter(e => !['Completed', 'Cancelled'].includes(e.status));
  const activeMatRequests = matRequests.filter(r => !['Completed', 'Cancelled'].includes(r.status));
  const activeWorkshops   = workshops.filter(w => !['Declined', 'Cancelled'].includes(w.status));
  const activeBookings    = bookings.filter(b => !['Completed', 'Cancelled'].includes(b.status));

  const allActive = [
    ...activeEngagements.map(item => ({ type: 'engagement', item })),
    ...activeMatRequests.map(item => ({ type: 'material',   item })),
    ...activeWorkshops.map(item   => ({ type: 'workshop',   item })),
    ...activeBookings.map(item    => ({ type: 'booking',    item })),
  ].sort((a, b) => new Date(b.item.createdAt) - new Date(a.item.createdAt));

  const totalActive = activeEngagements.length + activeMatRequests.length + activeWorkshops.length + activeBookings.length;

  const renderEmpty = (Icon, text, link, linkLabel) => (
    <div style={s.empty}>
      <div style={s.emptyIcon}><Icon size={40} strokeWidth={1.5} /></div>
      <div style={s.emptyText}>{text}</div>
      <a href={link} style={s.emptyBtn}>{linkLabel}</a>
    </div>
  );

  return (
    <PageShell title="My Requests" desc="Track the status of your submitted requests.">
      {loading && <div style={s.loading}>Loading your requests…</div>}
      {error   && <div style={s.error}><XCircle size={14} /> {error}</div>}

      {!loading && !error && (
        <>
          <div style={s.tabs}>
            <button style={s.tab(tab === 'all')}         onClick={() => setTab('all')}><Sparkles size={14} /> Active ({totalActive})</button>
            <button style={s.tab(tab === 'engagements')} onClick={() => setTab('engagements')}><Handshake size={14} /> Engagements ({engagements.length})</button>
            <button style={s.tab(tab === 'materials')}   onClick={() => setTab('materials')}><FileText size={14} /> Material Requests ({matRequests.length})</button>
            <button style={s.tab(tab === 'workshops')}   onClick={() => setTab('workshops')}><GraduationCap size={14} /> Workshops ({workshops.length})</button>
            <button style={s.tab(tab === 'bookings')}    onClick={() => setTab('bookings')}><CalendarDays size={14} /> Bookings ({bookings.length})</button>
          </div>

          {/* All active - a single combined view across every category */}
          {tab === 'all' && (
            allActive.length === 0 ? renderEmpty(Sparkles, "You're all caught up - no active requests right now.", '/', 'Back to Dashboard') : (
              <div style={s.grid}>{allActive.map(({ type, item }) => {
                const clickHandlers = {
                  engagement: () => setEngSelected(item),
                  material:   () => setMatSelected(item),
                  workshop:   () => setWsSelected(item),
                  booking:    () => setBookSelected(item),
                };
                const meta = TYPE_META[type];
                const Icon = type === 'engagement' ? (TYPE_ICON[item.engagementType] || Clipboard) : meta.icon;
                const title = {
                  engagement: item.accountName,
                  material:   item.assetType,
                  workshop:   item.workshopTitle || item.workshopId?.title,
                  booking:    item.topic,
                }[type];
                const dateMeta = type === 'booking'
                  ? new Date(item.preferredTime).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                  : new Date(item.createdAt).toLocaleDateString();

                return (
                  <div key={`${type}-${item._id}`} style={s.card} onClick={clickHandlers[type]}
                    onMouseEnter={el => el.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
                    onMouseLeave={el => el.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'}>
                    <div style={s.cardHeader}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon size={18} strokeWidth={1.75} style={{ color: '#555' }} />
                        <p style={s.cardTitle}>{title}</p>
                      </div>
                      <span style={s.statusBadge(item.status)}>{item.status}</span>
                    </div>
                    <div style={s.cardMeta}>
                      <span style={s.metaItem}><meta.icon size={12} /> {meta.label}</span>
                      <span style={s.metaItem}><Clock size={12} /> {dateMeta}</span>
                    </div>
                  </div>
                );
              })}</div>
            )
          )}

          {/* Engagements */}
          {tab === 'engagements' && (
            engagements.length === 0 ? renderEmpty(Inbox, 'No engagement requests yet.', '/engage', 'Engage RAAH') : <>
              <FilterBar items={engagements} filters={ENG_FILTERS} filter={engFilter} setFilter={setEngFilter} />
              <div style={s.grid}>{filteredEng.map(e => {
                const TypeIcon = TYPE_ICON[e.engagementType] || Clipboard;
                return (
                <div key={e._id} style={s.card} onClick={() => setEngSelected(e)}
                  onMouseEnter={el => el.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
                  onMouseLeave={el => el.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'}>
                  <div style={s.cardHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <TypeIcon size={18} strokeWidth={1.75} style={{ color: '#555' }} />
                      <p style={s.cardTitle}>{e.accountName}</p>
                    </div>
                    <span style={s.statusBadge(e.status)}>{e.status}</span>
                  </div>
                  <div style={s.cardMeta}>
                    {e.engagementType && <span style={s.metaItem}><Tag size={12} /> {e.engagementType}</span>}
                    {e.product        && <span style={s.metaItem}><Package size={12} /> {e.product}</span>}
                    {e.raahProduct    && <span style={s.metaItem}><Puzzle size={12} /> {e.raahProduct}</span>}
                    {e.urgency        && <span style={s.metaItem}><Zap size={12} /> {e.urgency}</span>}
                    <span style={s.metaItem}><Clock size={12} /> {new Date(e.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div style={s.summary}>{e.opportunitySummary}</div>
                  <a href={`/my-requests/${e._id}/track`} style={s.trackBtn}
                    onClick={ev => { ev.stopPropagation(); ev.preventDefault(); navigate(`/my-requests/${e._id}/track`); }}>
                    <MapPin size={13} /> Track Engagement
                  </a>
                  {isAdmin && e.planeIssueUrl && <a href={e.planeIssueUrl} target="_blank" rel="noreferrer" style={{ ...s.planeLink, marginLeft: '12px' }} onClick={ev => ev.stopPropagation()}><ExternalLink size={12} /> Plane #{e.planeSequenceId}</a>}
                </div>
              );})}</div>
            </>
          )}

          {/* Material requests */}
          {tab === 'materials' && (
            matRequests.length === 0 ? renderEmpty(Inbox, 'No material requests yet.', '/materials', 'Request Materials') : <>
              <FilterBar items={matRequests} filters={MAT_FILTERS} filter={matFilter} setFilter={setMatFilter} />
              <div style={s.grid}>{filteredMat.map(r => (
                <div key={r._id} style={s.card} onClick={() => setMatSelected(r)}
                  onMouseEnter={el => el.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
                  onMouseLeave={el => el.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'}>
                  <div style={s.cardHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FileText size={18} strokeWidth={1.75} style={{ color: '#555' }} />
                      <p style={s.cardTitle}>{r.assetType}</p>
                    </div>
                    <span style={s.statusBadge(r.status)}>{r.status}</span>
                  </div>
                  <div style={s.cardMeta}>
                    {r.fulfilledMaterialId && <span style={{ ...s.metaItem, color: '#2f9e44', fontWeight: '600' }}><Paperclip size={12} /> Material ready</span>}
                    {r.useCase  && <span style={s.metaItem}><Target size={12} /> {r.useCase}</span>}
                    {r.deadline && <span style={s.metaItem}><CalendarDays size={12} /> Due {r.deadline}</span>}
                    <span style={s.metaItem}><Clock size={12} /> {new Date(r.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div style={s.summary}>{r.customerContext}</div>
                </div>
              ))}</div>
            </>
          )}

          {/* Workshops */}
          {tab === 'workshops' && (
            workshops.length === 0 ? renderEmpty(GraduationCap, 'No workshop registrations yet.', '/workshops', 'Browse Workshops') : <>
              <FilterBar items={workshops} filters={WS_FILTERS} filter={wsFilter} setFilter={setWsFilter} />
              <div style={s.grid}>{filteredWs.map(w => (
                <div key={w._id} style={s.card} onClick={() => setWsSelected(w)}
                  onMouseEnter={el => el.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
                  onMouseLeave={el => el.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'}>
                  <div style={s.cardHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <GraduationCap size={18} strokeWidth={1.75} style={{ color: '#555' }} />
                      <p style={s.cardTitle}>{w.workshopTitle || w.workshopId?.title}</p>
                    </div>
                    <span style={s.statusBadge(w.status)}>{w.status}</span>
                  </div>
                  <div style={s.cardMeta}>
                    {w.workshopId?.date            && <span style={s.metaItem}><CalendarDays size={12} /> {formatWorkshopDate(w.workshopId.date)}</span>}
                    {w.workshopId?.displayLocation && <span style={s.metaItem}><MapPin size={12} /> {w.workshopId.displayLocation}</span>}
                    {w.workshopId?.product         && <span style={s.metaItem}><Package size={12} /> {w.workshopId.product}</span>}
                    <span style={s.metaItem}><Clock size={12} /> {new Date(w.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div style={s.summary}>{w.workshopId?.description}</div>
                </div>
              ))}</div>
            </>
          )}
        </>
      )}

      {/* Bookings tab */}
          {tab === 'bookings' && (
            bookings.length === 0 ? renderEmpty(CalendarDays, 'No bookings yet.', '/booking', 'Book Time') : <>
              <FilterBar items={bookings} filters={['All', 'Confirmed', 'Completed', 'Cancelled']} filter={bookFilter} setFilter={setBookFilter} statusKey="status" />
              <div style={s.grid}>{(bookFilter === 'All' ? bookings : bookings.filter(b => b.status === bookFilter)).map(b => (
                <div key={b._id} style={s.card} onClick={() => setBookSelected(b)}
                  onMouseEnter={el => el.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
                  onMouseLeave={el => el.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'}>
                  <div style={s.cardHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CalendarDays size={18} strokeWidth={1.75} style={{ color: '#555' }} />
                      <p style={s.cardTitle}>{b.topic}</p>
                    </div>
                    <span style={s.statusBadge(b.status)}>{b.status}</span>
                  </div>
                  <div style={s.cardMeta}>
                    {b.teamLabel && <span style={s.metaItem}><Users size={12} /> {b.teamLabel}</span>}
                    <span style={s.metaItem}><Clock size={12} /> {new Date(b.preferredTime).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  {b.joinUrl && (
                    <a href={b.joinUrl} target="_blank" rel="noreferrer" style={s.planeLink} onClick={ev => ev.stopPropagation()}>
                      <Video size={14} /> Join Teams Meeting
                    </a>
                  )}
                </div>
              ))}</div>
            </>
          )}

      {engSelected  && <EngagementModal      item={engSelected}  onClose={() => setEngSelected(null)} isAdmin={isAdmin} />}
      {matSelected  && <MaterialRequestModal item={matSelected}  onClose={() => setMatSelected(null)} isAdmin={isAdmin} />}
      {wsSelected   && <WorkshopModal        item={wsSelected}   onClose={() => setWsSelected(null)}  />}
      {bookSelected && (
        <BookingModal item={bookSelected} getToken={getToken} onClose={() => setBookSelected(null)}
          onCancelled={(id) => setBookings(prev => prev.map(b => b._id === id ? { ...b, status: 'Cancelled' } : b))} />
      )}
    </PageShell>
  );
}
